import { FC } from 'react';
import { useForm } from 'react-hook-form';
import toast from 'react-hot-toast';

import { useAddMentorMutation } from '../services/apiSlice';
import { AddMentorFormData, emptyFunction } from '../types';
import { emailPattern } from '../constants';

interface Properties {
  onSuccess: typeof emptyFunction;
}

const AddMentorForm: FC<Properties> = ({ onSuccess }) => {
  const {
    handleSubmit,
    register,
    formState: { errors },
  } = useForm<AddMentorFormData>({
    defaultValues: {
      firstName: '',
      lastName: '',
      email: '',
      phone: '',
    },
  });
  const [addMentor, { isLoading }] = useAddMentorMutation();

  const handleAddMentor = async (formData: AddMentorFormData) => {
    try {
      await addMentor({
        ...formData,
        phone: formData.phone || undefined,
      }).unwrap();
    } catch (error: any) {
      const errorMessage =
        error?.data?.message ||
        'Some unexpected error happened while trying to add the mentor. Please try again later!';
      toast.error(errorMessage);
      return;
    }
    onSuccess();
  };

  return (
    <div className="z-20 w-full">
      <div className="text-sm text-slate-500 break-words mb-4">
        This form allows you to add a new <strong>Mentor</strong>.
        <br />
        When you submit the form, the system will send a welcome email with a
        password reset link to allow the mentor to set their password and log
        in.
      </div>
      <div className="grid grid-flow-row bg-gray-100 rounded-xl p-4">
        <form
          onSubmit={handleSubmit(handleAddMentor)}
          className="grid w-full gap-2"
        >
          <div className="font-medium">First name</div>
          <input
            className="w-full p-2 border rounded-md"
            {...register('firstName', {
              required: true,
              minLength: 2,
              maxLength: 50,
            })}
            placeholder="First Name"
          />
          {errors.firstName && (
            <p className="text-sm text-rose-600">
              Please enter a valid first name.
            </p>
          )}
          <div className="font-medium">Last name</div>
          <input
            className="w-full p-2 border rounded-md"
            {...register('lastName', {
              required: true,
              minLength: 2,
              maxLength: 50,
            })}
            placeholder="Last Name"
          />
          {errors.lastName && (
            <p className="text-sm text-rose-600">
              Please enter a valid last name.
            </p>
          )}
          <div className="font-medium">Email</div>
          <input
            className="w-full p-2 border rounded-md"
            {...register('email', {
              required: true,
              pattern: emailPattern,
            })}
            placeholder="Email Address"
          />
          {errors.email && (
            <p className="text-sm text-rose-600">
              Please enter a valid email address.
            </p>
          )}
          <div className="font-medium">Phone (optional)</div>
          <input
            className="w-full p-2 border rounded-md"
            {...register('phone', {
              required: false,
              pattern:
                /^\+?\(?\d{1,3}\)?[\s.-]?\(?\d{1,4}\)?[\s.-]?\d{1,4}[\s.-]?\d{1,9}$/,
            })}
            placeholder="Phone Number"
          />
          {errors.phone && (
            <p className="text-sm text-rose-600">
              Please enter a valid phone number.
            </p>
          )}
          <button
            type="submit"
            className="justify-self-center max-w-[90%] mt-6 py-2.5 px-2 bg-tttDefault text-white font-bold text-xs uppercase rounded shadow-md hover:bg-blue-600 hover:shadow-lg disabled:opacity-50"
            disabled={isLoading}
          >
            Add Mentor
          </button>
        </form>
      </div>
    </div>
  );
};

export default AddMentorForm;
